import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { rgbaEquals, type Rgba } from '../../sim/color.ts';
import {
  BOAT_BEAM_MIN_M,
  BOAT_DRAFT_MAX_M,
  BOAT_DRAFT_MIN_M,
  BOAT_LENGTH_MAX_M,
  BOAT_LENGTH_MIN_M,
  BOAT_MEASURE_STEP_M,
  BOAT_PLANING_MAX_KMH,
  BOAT_PLANING_MIN_KMH,
  BOAT_REACH_MAX_M,
  BOAT_REACH_MIN_M,
  createSkiffBoat,
  FITTINGS,
  maxBeamM,
  SIDE_VIEW_PARTS,
  type BoatColorKey,
  type BoatDocument,
  type BoatNumberKey,
  type FittingKey,
} from '../../sim/boat/index.ts';
import { ColorPicker } from '../colorPicker.tsx';
import { ScrubNumber } from '../scrubNumber.tsx';
import { Tooltip } from '../tooltip.tsx';
import type { TestChoice } from '../../game/testPlay.ts';
import { RING_MODES, type RingMode } from '../../sim/replay.ts';
import { EditorIcon, RedoIcon, UndoIcon } from '../editor/icons.tsx';
import { setPanel } from '../panels.ts';
import type { UiToastTone } from '../toast.ts';
import { BoatNameField } from './nameField.tsx';
import '../editor/toolbar.css';
import { TelemetryHud, type TelemetryStripState } from '../play/telemetryStrip.tsx';
import { BoatTrialCard } from './trialCard.tsx';

const MESSAGE_TIMEOUT_MS = 4000;

export type BoatCatalogChoice = {
  id: string;
  label: string;
};

export type BoatWorkshopState = {
  boat: BoatDocument;
  fileName: string;
  boats: readonly BoatCatalogChoice[];
  compareIds: readonly string[];
  showGrid: boolean;
  ringMode: RingMode;
  currentBoatId: string | null;
  canUndo: boolean;
  canRedo: boolean;
  canResetPose: boolean;
  canDelete: boolean;
  readout: TelemetryStripState;
  testMaps: readonly TestChoice[];
  message: string | null;
  messageTone: UiToastTone;
};

export type BoatWorkshopActions = {
  onNumber: (key: BoatNumberKey, value: number) => void;
  onColor: (key: BoatColorKey, value: Rgba) => void;
  onColorCommit: () => void;
  onColorCancel: () => void;
  onToggleFitting: (key: FittingKey) => void;
  onRename: (raw: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onNew: () => void;
  onImport: () => void;
  onOpenBoat: (id: string) => void;
  onToggleCompare: (id: string) => void;
  onToggleGrid: () => void;
  onSetRingMode: (mode: RingMode) => void;
  onResetSkiff: () => void;
  onResetPose: () => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
  onTestMap: (id: string) => void;
  refreshTestMaps: () => void;
};

export type BoatWorkshopProps = {
  state: BoatWorkshopState;
  actions: BoatWorkshopActions;
};

function isSkiffStats(boat: BoatDocument): boolean {
  const skiff = createSkiffBoat();

  return (
    boat.lengthM === skiff.lengthM &&
    boat.beamM === skiff.beamM &&
    boat.draftM === skiff.draftM &&
    boat.planingSpeedKmh === skiff.planingSpeedKmh &&
    boat.turnM === skiff.turnM &&
    boat.accelM === skiff.accelM &&
    boat.brakeM === skiff.brakeM &&
    rgbaEquals(boat.hull, skiff.hull) &&
    rgbaEquals(boat.deck, skiff.deck) &&
    rgbaEquals(boat.accent, skiff.accent) &&
    SIDE_VIEW_PARTS.every((part) =>
      rgbaEquals(boat.side[part.key], skiff.side[part.key]),
    ) &&
    FITTINGS.every(
      (fitting) => boat.fittings[fitting.key] === skiff.fittings[fitting.key],
    )
  );
}

function MenuItem({
  label,
  shortcut,
  disabled,
  pressed,
  onClick,
}: {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      class="editor-menu-item"
      role="menuitem"
      disabled={disabled}
      aria-pressed={
        pressed === undefined ? undefined : pressed ? 'true' : 'false'
      }
      onClick={onClick}
    >
      <span>{label}</span>
      {shortcut ? (
        <span class="editor-toolbar-shortcut">{shortcut}</span>
      ) : null}
    </button>
  );
}

type WorkshopMenu =
  | 'file'
  | 'boats'
  | 'color'
  | 'fittings'
  | 'compare'
  | 'view'
  | 'ring'
  | 'test';

export function BoatWorkshopPanel({
  state,
  actions,
}: BoatWorkshopProps): JSX.Element {
  const [open, setOpen] = useState<WorkshopMenu | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const { boat } = state;

  useEffect(() => {
    if (open === null) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      const node = barRef.current;
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (
        node?.contains(target) ||
        document.querySelector('.color-picker-dialog')?.contains(target)
      ) {
        return;
      }

      setOpen(null);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !document.querySelector('.color-picker-dialog')) {
        setOpen(null);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id: WorkshopMenu): void => {
    setOpen((current) => (current === id ? null : id));
  };

  const run = (action: () => void): void => {
    action();
    setOpen(null);
  };

  const others = state.boats.filter(
    (entry) => entry.id !== state.currentBoatId,
  );

  return (
    <div class="editor-toolbar" role="toolbar" aria-label="Boat workshop">
      <div ref={barRef} class="editor-menubar ui-panel ui-chrome">
        <div class="editor-menubar-left">
          <div
            class="editor-menubar-menus"
            role="menubar"
            aria-label="Boat workshop"
          >
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'file' ? 'true' : 'false'}
                onClick={() => toggle('file')}
              >
                File
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'file'}
              >
                <MenuItem label="New" onClick={() => run(actions.onNew)} />
                <MenuItem
                  label="Import"
                  onClick={() => run(actions.onImport)}
                />
                <MenuItem
                  label="Save"
                  shortcut="Ctrl+S"
                  onClick={() => run(actions.onSave)}
                />
                <MenuItem
                  label="Delete"
                  disabled={!state.canDelete}
                  onClick={() => run(actions.onDelete)}
                />
                <MenuItem label="Close" onClick={() => run(actions.onClose)} />
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'boats' ? 'true' : 'false'}
                onClick={() => toggle('boats')}
              >
                Boats
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'boats'}
              >
                {state.boats.length === 0 ? (
                  <MenuItem
                    label="No boats"
                    disabled
                    onClick={() => undefined}
                  />
                ) : (
                  state.boats.map((entry) => (
                    <MenuItem
                      key={entry.id}
                      label={entry.label}
                      pressed={state.currentBoatId === entry.id}
                      onClick={() => run(() => actions.onOpenBoat(entry.id))}
                    />
                  ))
                )}
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'color' ? 'true' : 'false'}
                onClick={() => toggle('color')}
              >
                Color
              </button>
              <div
                class="editor-menu-panel ui-panel boat-color-menu"
                role="menu"
                hidden={open !== 'color'}
              >
                {SIDE_VIEW_PARTS.map((part) => (
                  <ColorPicker
                    key={part.key}
                    label={part.label}
                    value={boat.side[part.key]}
                    onChange={(value) => actions.onColor(part.key, value)}
                    onCommit={actions.onColorCommit}
                    onCancel={actions.onColorCancel}
                  />
                ))}
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'fittings' ? 'true' : 'false'}
                onClick={() => toggle('fittings')}
              >
                Fittings
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'fittings'}
              >
                {FITTINGS.map((fitting) => (
                  <MenuItem
                    key={fitting.key}
                    label={fitting.label}
                    pressed={boat.fittings[fitting.key]}
                    onClick={() => actions.onToggleFitting(fitting.key)}
                  />
                ))}
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'compare' ? 'true' : 'false'}
                onClick={() => toggle('compare')}
              >
                Compare
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'compare'}
              >
                {others.length === 0 ? (
                  <MenuItem
                    label="No other boats"
                    disabled
                    onClick={() => undefined}
                  />
                ) : (
                  others.map((entry) => (
                    <MenuItem
                      key={entry.id}
                      label={entry.label}
                      pressed={state.compareIds.includes(entry.id)}
                      onClick={() => actions.onToggleCompare(entry.id)}
                    />
                  ))
                )}
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'view' ? 'true' : 'false'}
                onClick={() => toggle('view')}
              >
                View
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'view'}
              >
                <MenuItem
                  label="Grid"
                  pressed={state.showGrid}
                  onClick={() => actions.onToggleGrid()}
                />
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-label="Ring"
                aria-haspopup="true"
                aria-expanded={open === 'ring' ? 'true' : 'false'}
                title="P cycles Off, Planing, and Headreach."
                onClick={() => toggle('ring')}
              >
                Ring
                <span class="editor-toolbar-shortcut" aria-hidden="true">
                  P
                </span>
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'ring'}
              >
                {RING_MODES.map((mode) => (
                  <MenuItem
                    key={mode}
                    label={mode === 'off' ? 'Off' : mode === 'planing' ? 'Planing' : 'Headreach'}
                    pressed={state.ringMode === mode}
                    onClick={() => run(() => actions.onSetRingMode(mode))}
                  />
                ))}
              </div>
            </div>
            <div class="editor-menu">
              <button
                type="button"
                class="editor-menu-trigger"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={open === 'test' ? 'true' : 'false'}
                onClick={() => {
                  if (open !== 'test') {
                    actions.refreshTestMaps();
                  }

                  toggle('test');
                }}
              >
                Test
              </button>
              <div
                class="editor-menu-panel ui-panel"
                role="menu"
                hidden={open !== 'test'}
              >
                {state.testMaps.length === 0 ? (
                  <MenuItem
                    label="No maps"
                    disabled
                    onClick={() => undefined}
                  />
                ) : (
                  state.testMaps.map((entry) => (
                    <MenuItem
                      key={entry.id}
                      label={entry.label}
                      disabled={entry.disabled}
                      onClick={() => run(() => actions.onTestMap(entry.id))}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
          <div class="editor-menubar-history">
            <Tooltip label="Undo" shortcut="Ctrl+Z">
              <button
                type="button"
                class="editor-rail-button"
                aria-label="Undo"
                disabled={!state.canUndo}
                onClick={actions.onUndo}
              >
                <EditorIcon icon={UndoIcon} />
              </button>
            </Tooltip>
            <Tooltip label="Redo" shortcut="Ctrl+Y">
              <button
                type="button"
                class="editor-rail-button"
                aria-label="Redo"
                disabled={!state.canRedo}
                onClick={actions.onRedo}
              >
                <EditorIcon icon={RedoIcon} />
              </button>
            </Tooltip>
            <button
              type="button"
              class="ui-button editor-menubar-action"
              disabled={!state.canResetPose}
              onClick={actions.onResetPose}
            >
              Reset position
            </button>
          </div>
        </div>
        <div class="editor-menubar-center">
          <BoatNameField
            name={boat.name}
            fileName={state.fileName}
            onRename={actions.onRename}
          />
        </div>
        <p class="editor-menubar-brand">Helmway Boats</p>
      </div>
      <div class="editor-options ui-panel ui-chrome is-open">
        <div class="editor-options-scroll">
          <ScrubNumber
            label="Length"
            value={boat.lengthM}
            min={BOAT_LENGTH_MIN_M}
            max={BOAT_LENGTH_MAX_M}
            step={BOAT_MEASURE_STEP_M}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('lengthM', value)}
          />
          <ScrubNumber
            label="Beam"
            value={boat.beamM}
            min={BOAT_BEAM_MIN_M}
            max={maxBeamM(boat.lengthM)}
            step={BOAT_MEASURE_STEP_M}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('beamM', value)}
          />
          <ScrubNumber
            label="Draft"
            value={boat.draftM}
            min={BOAT_DRAFT_MIN_M}
            max={BOAT_DRAFT_MAX_M}
            step={BOAT_MEASURE_STEP_M}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('draftM', value)}
          />
          <ScrubNumber
            label="Planing speed"
            value={boat.planingSpeedKmh}
            min={BOAT_PLANING_MIN_KMH}
            max={BOAT_PLANING_MAX_KMH}
            step={1}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('planingSpeedKmh', value)}
          />
          <ScrubNumber
            label="Turn"
            value={boat.turnM}
            min={BOAT_REACH_MIN_M}
            max={BOAT_REACH_MAX_M}
            step={1}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('turnM', value)}
          />
          <ScrubNumber
            label="Accelerate"
            value={boat.accelM}
            min={BOAT_REACH_MIN_M}
            max={BOAT_REACH_MAX_M}
            step={1}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('accelM', value)}
          />
          <ScrubNumber
            label="Brake"
            value={boat.brakeM}
            min={BOAT_REACH_MIN_M}
            max={BOAT_REACH_MAX_M}
            step={1}
            openOnFocus={true}
            showEnds={true}
            onChange={(value) => actions.onNumber('brakeM', value)}
          />
          <button
            type="button"
            class="ui-button"
            disabled={isSkiffStats(boat)}
            onClick={actions.onResetSkiff}
          >
            Reset
          </button>
        </div>
      </div>
      <div class="boat-dock">
        <BoatTrialCard boat={boat} />
        <TelemetryHud state={state.readout} placement="pond" />
      </div>
      <p
        class="ui-toast editor-toolbar-message ui-chrome"
        data-tone={state.messageTone}
        role="status"
        hidden={state.message === null}
      >
        {state.message}
      </p>
    </div>
  );
}

export type BoatWorkshop = {
  sync: () => void;
  showMessage: (text: string, tone: UiToastTone) => void;
  destroy: () => void;
};

export function mountBoatWorkshop(options: {
  getState: () => Omit<BoatWorkshopState, 'message' | 'messageTone'>;
  actions: BoatWorkshopActions;
}): BoatWorkshop {
  let message: string | null = null;
  let messageTone: UiToastTone = 'warning';
  let messageTimer: ReturnType<typeof setTimeout> | null = null;

  const push = (): void => {
    setPanel('boatWorkshop', {
      state: { ...options.getState(), message, messageTone },
      actions: options.actions,
    });
  };

  push();

  return {
    sync: push,
    showMessage: (text, tone) => {
      message = text;
      messageTone = tone;
      push();

      if (messageTimer !== null) {
        clearTimeout(messageTimer);
      }

      messageTimer = setTimeout(() => {
        message = null;
        messageTimer = null;
        push();
      }, MESSAGE_TIMEOUT_MS);
    },
    destroy: () => {
      if (messageTimer !== null) {
        clearTimeout(messageTimer);
      }

      setPanel('boatWorkshop', null);
    },
  };
}
